import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { projects, stats } from '../data/mockData';

gsap.registerPlugin(ScrollTrigger);

const IMG = 'https://imagedelivery.net/xaKlCos5cTg_1RWzIu_h-A';
const WORKSHOP = `${IMG}/fb4fd00d-9b0b-43f3-46b9-256a815c9b00/publicContain`;
const EASE = 'cubic-bezier(0.23,1,0.32,1)';

/* ─── Hero ─── */
function Hero() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const tl = gsap.timeline();
    tl.fromTo(el.querySelector('h1'), { opacity: 0, y: 32 }, { opacity: 1, y: 0, duration: 0.8, ease: EASE })
      .fromTo(el.querySelector('p'),  { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.65, ease: EASE }, '-=0.45');
    return () => tl.kill();
  }, []);

  return (
    <section
      ref={ref}
      className="relative flex items-center overflow-hidden"
      style={{ minHeight: '55vh' }}
      aria-label="Proyectos realizados Del Sol Muebles"
    >
      <div className="absolute inset-0">
        <img
          src={WORKSHOP}
          alt="Taller de carpintería Del Sol Muebles en Tonalá, Jalisco"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-wood/90 via-wood/65 to-wood/30" />
      </div>

      <div className="relative z-10 container-custom py-24">
        <div className="max-w-xl">
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-gold mb-5 block">
            Portfolio · Tonalá, Jalisco
          </span>
          <h1 className="font-serif text-display-xs md:text-display-sm text-white mb-6 leading-tight">
            Proyectos Realizados
          </h1>
          <div className="w-14 h-px bg-gold mb-6" />
          <p className="font-sans text-base text-cream/80 leading-relaxed">
            Cada mueble cuenta una historia — la de quienes lo eligieron.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Project Grid ─── */
const TYPE_COLORS = {
  Restaurante:          'bg-terracotta/10 text-terracotta',
  Hogar:                'bg-gold/10 text-gold-dark',
  'Proyecto Especial':  'bg-wood/10 text-wood',
  Exterior:             'bg-green-50 text-green-700',
};

function ProjectGrid() {
  const ref = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const cards = el.querySelectorAll('[data-card]');
    cards.forEach((card, i) => {
      gsap.fromTo(
        card,
        { opacity: 0, y: 28 },
        {
          opacity: 1,
          y: 0,
          duration: 0.65,
          delay: (i % 3) * 0.1,
          ease: EASE,
          scrollTrigger: {
            trigger: card,
            start: 'top 90%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, []);

  return (
    <section ref={ref} className="section-padding bg-cream-light">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="font-sans text-xs tracking-[0.25em] uppercase text-terracotta/80 mb-4 block">
            Nuestro Trabajo
          </span>
          <h2 className="font-serif text-display-xs md:text-display-sm text-wood">
            Espacios que Hemos Transformado
          </h2>
          <div className="w-12 h-px bg-gold mx-auto mt-6" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {projects.map((proj) => (
            <article
              key={proj.id}
              data-card
              className="group relative rounded-sm overflow-hidden cursor-default"
              itemScope
              itemType="https://schema.org/CreativeWork"
            >
              {/* Image */}
              <div className="aspect-video overflow-hidden bg-cream">
                <img
                  src={proj.image}
                  alt={proj.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  itemProp="image"
                />
              </div>

              {/* Location badge on hover */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-sm font-sans text-[10px] text-wood">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                  {proj.location}
                </span>
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-wood/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none" />

              {/* Info */}
              <div className="p-4 bg-cream">
                <span
                  className={`inline-block font-sans text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm mb-2 ${TYPE_COLORS[proj.type] || 'bg-taupe/10 text-taupe'}`}
                >
                  {proj.type}
                </span>
                <h3 className="font-serif text-base md:text-lg text-wood leading-snug mb-1 group-hover:text-terracotta transition-colors duration-300" itemProp="name">
                  {proj.name}
                </h3>
                <p className="font-sans text-xs text-taupe leading-relaxed line-clamp-2" itemProp="description">
                  {proj.description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Stats ─── */
const FALLBACK_STATS = [
  { value: '500+', label: 'Proyectos Completados' },
  { value: '20+',  label: 'Años de Experiencia' },
  { value: '32',   label: 'Estados Atendidos' },
  { value: '100%', label: 'Madera Maciza' },
];

function StatsSection() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const items = el.querySelectorAll('[data-stat]');
    items.forEach((item, i) => {
      gsap.fromTo(
        item,
        { opacity: 0, y: 20 },
        {
          opacity: 1, y: 0, duration: 0.6, delay: i * 0.12, ease: EASE,
          scrollTrigger: { trigger: item, start: 'top 88%', toggleActions: 'play none none none' },
        }
      );
    });
    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, []);

  const displayStats = (Array.isArray(stats) && stats.length > 0) ? stats : FALLBACK_STATS;

  return (
    <section ref={ref} className="py-20 bg-wood">
      <div className="container-custom">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {displayStats.map((stat, i) => (
            <div key={i} data-stat>
              <p className="font-serif text-display-xs md:text-display-sm text-gold">{stat.value}</p>
              <p className="font-sans text-xs tracking-widest uppercase text-cream/60 mt-2">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Custom Project CTA ─── */
function CustomProjectCTA() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    gsap.fromTo(
      el,
      { opacity: 0, y: 24 },
      {
        opacity: 1, y: 0, duration: 0.7, ease: EASE,
        scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' },
      }
    );
    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, []);

  return (
    <section className="section-padding bg-cream">
      <div ref={ref} className="container-custom max-w-2xl mx-auto text-center">
        <span className="font-sans text-xs tracking-[0.25em] uppercase text-terracotta/80 mb-4 block">
          Siguiente Paso
        </span>
        <h2 className="font-serif text-display-xs md:text-display-sm text-wood mb-6">
          ¿Tienes un Proyecto en Mente?
        </h2>
        <div className="w-12 h-px bg-gold mx-auto mb-8" />
        <p className="font-sans text-base text-taupe leading-relaxed mb-10">
          Desde una sola pieza hasta el equipamiento completo de tu espacio, diseñamos y
          fabricamos a tu medida. Madera maciza, acabados artesanales, entrega a todo México.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/coleccion" className="btn-outline">
            Ver Catálogo
          </Link>
          <a
            href="https://wa.me/523338283729?text=Hola%20Del%20Sol%20Muebles%2C%20quiero%20cotizar%20un%20proyecto%20personalizado"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Cotizar Proyecto
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export default function Proyectos() {
  return (
    <main>
      <Hero />
      <ProjectGrid />
      <StatsSection />
      <CustomProjectCTA />
    </main>
  );
}
